// lib/generation-output-scope.ts
import type { GenerationOutputScope } from '#types/types';

type LegacyGenerationScopeFields = {
  workspaceId?: unknown;
  projectId?: unknown;
};

const normalizeId = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * Resolves the immutable output destination carried by a generation task. An explicit malformed
 * scope is refused rather than silently falling back to a legacy field; legacy tasks are upgraded
 * from their workspaceId so an old task cannot drift to the current project or workspace.
 */
export const normalizeGenerationOutputScope = (
  value: unknown,
  legacy: LegacyGenerationScopeFields = {},
): GenerationOutputScope | null => {
  if (value !== undefined && value !== null) {
    if (!isRecord(value) || (value.kind !== 'WORKSPACE' && value.kind !== 'PROJECT')) return null;
    if (value.kind === 'WORKSPACE') {
      const workspaceId = normalizeId(value.workspaceId);
      return workspaceId ? { kind: 'WORKSPACE', workspaceId } : null;
    }
    const projectId = normalizeId(value.projectId);
    return projectId ? { kind: 'PROJECT', projectId } : null;
  }

  const workspaceId = normalizeId(legacy.workspaceId);
  if (workspaceId) return { kind: 'WORKSPACE', workspaceId };

  const projectId = normalizeId(legacy.projectId);
  return projectId ? { kind: 'PROJECT', projectId } : null;
};

export const resolveGenerationOutputScopeForView = (
  projectIdInput: string | null | undefined,
  workspaceIdInput: string | null | undefined,
  viewMode: string | null | undefined,
): GenerationOutputScope | null => {
  const projectId = normalizeId(projectIdInput);
  const workspaceId = normalizeId(workspaceIdInput);
  if (!projectId) return null;
  return viewMode === 'marketing'
    ? { kind: 'PROJECT', projectId }
    : workspaceId
      ? { kind: 'WORKSPACE', workspaceId }
      : null;
};

export type GenerationRouteOutputScopeValidation =
  | { supported: true; scope: GenerationOutputScope }
  | { supported: false; reason: 'INVALID' };

/**
 * Shape validation precedes route authorization; PROJECT results are materialized on the server.
 */
export const validateGenerationRouteOutputScope = (
  value: unknown,
  legacy: LegacyGenerationScopeFields = {},
): GenerationRouteOutputScopeValidation => {
  const scope = normalizeGenerationOutputScope(value, legacy);
  if (!scope) return { supported: false, reason: 'INVALID' };
  return { supported: true, scope };
};

export const areGenerationOutputScopesEqual = (
  left: GenerationOutputScope | null | undefined,
  right: GenerationOutputScope | null | undefined,
): boolean => {
  if (!left || !right || left.kind !== right.kind) return false;
  return 'workspaceId' in left
    ? 'workspaceId' in right && left.workspaceId === right.workspaceId
    : 'projectId' in right && left.projectId === right.projectId;
};

export type { LegacyGenerationScopeFields };
